import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMyPackageApi, fetchPackagesApi, purchasePackageApi } from '../../api/packages.api';
import { PublicPageShell } from '../../components/layout/PublicPageShell';
import { PageSeo } from '../../components/seo/PageSeo';
import { useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { useToast } from '../../hooks/useToast';
import { buildLocalizedPath } from '../../seo/urls';
import { useOpenSettings } from '../settings/SettingsModalContext';
import { PricingFaq } from './PricingFaq';
import { PricingPlans } from './PricingPlans';
import { PricingSeoContent } from './PricingSeoContent';
import { resolvePlanAction } from './pricingConstants';

export const PricingPage = () => {
  const { t, language } = useI18n();
  const toast = useToast();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const openSettings = useOpenSettings();
  const faqItems = t('pricingFaq') || [];
  const [packages, setPackages] = useState([]);
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const items = await fetchPackagesApi();
        if (!cancelled) {
          setPackages(items);
        }
        if (isAuthenticated) {
          try {
            const current = await fetchMyPackageApi();
            if (!cancelled) {
              setEntitlement(current);
            }
          } catch {
            if (!cancelled) {
              setEntitlement(null);
            }
          }
        } else if (!cancelled) {
          setEntitlement(null);
        }
      } catch {
        if (!cancelled) {
          setPackages([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleBuy = async (pkg, payFromWallet) => {
    if (!isAuthenticated) {
      window.location.assign('/login');
      return;
    }

    if (resolvePlanAction(pkg, entitlement).disabled) {
      return;
    }

    setBuyingId(pkg.id);
    try {
      const result = await purchasePackageApi({
        packageId: pkg.id,
        payFromWallet,
        organizationName: pkg.kind === 'Organization' ? pkg.nameFa : undefined
      });
      if (result?.paymentUrl || result?.PaymentUrl) {
        window.location.assign(result.paymentUrl || result.PaymentUrl);
        return;
      }
      openSettings('packages');
    } catch (error) {
      toast.error(error.message || t('packagePurchaseFailed'));
    } finally {
      setBuyingId(null);
    }
  };

  return (
    <PublicPageShell activePath="/pricing">
      <PageSeo page="pricing" language={language} faqItems={faqItems} />
      <PricingSeoContent />
      <section className="flex-1 py-10 sm:py-14">
        <PricingPlans packages={packages} loading={loading} buyingId={buyingId} onBuy={handleBuy} entitlement={entitlement} />
        <PricingFaq />

        <div className="mx-auto mt-16 max-w-3xl rounded-[1.75rem] border border-border bg-accent px-6 py-10 text-center text-white shadow-floating dark:text-slate-950 sm:px-10">
          <h2 className="text-2xl font-black tracking-[-0.04em] sm:text-3xl">{t('pricingFinalCtaTitle')}</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-white/75 dark:text-slate-950/65">
            {t('pricingFinalCtaLead')}
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex min-h-12 cursor-pointer items-center justify-center rounded-2xl bg-white px-6 text-sm font-bold text-accent transition hover:bg-white/90 dark:bg-slate-950 dark:text-accent dark:hover:bg-slate-900"
            >
              {t('pricingPurchaseTitle')}
            </button>
            <Link
              to={isAuthenticated ? '/projects' : buildLocalizedPath('/', language)}
              className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-white/25 px-6 text-sm font-bold text-white/90 transition hover:bg-white/10 dark:border-slate-950/20 dark:text-slate-950 dark:hover:bg-slate-950/8"
            >
              {t('pricingPrimaryCta')}
            </Link>
          </div>
        </div>
      </section>
    </PublicPageShell>
  );
};
